import { URLS } from "@core/shared/utils/urls";
import { useIsLoggedIn } from "@framework/account/hooks";
import { useEffect, useState } from "react";
import {
  type OfflineDue,
  offlineDuesSignature,
  readOfflineDues,
} from "../utils/offline-dues";

const dismissedKey = "mntrm-offline-dues-dismissed";

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(dismissedKey);
  } catch {
    return null;
  }
}

/**
 * Unpaid attended sessions of monthly groups for the signed-in student.
 * Loads after the first render (the server render shows nothing). Hiding it
 * lasts for this browser session, and only until the dues change.
 *
 * @example
 * const { months, isVisible, hide } = useOfflineDues();
 */
export function useOfflineDues() {
  const isLoggedIn = useIsLoggedIn();
  const [months, setMonths] = useState<OfflineDue[]>([]);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setMonths([]);
      return;
    }

    let active = true;

    setDismissed(readDismissed());

    fetch(URLS.sessions.dues)
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (active) setMonths(readOfflineDues(data));
      })
      .catch(() => {
        // Silent: no strip when the dues cannot be loaded.
      });

    return () => {
      active = false;
    };
  }, [isLoggedIn]);

  const signature = offlineDuesSignature(months);

  const hide = () => {
    try {
      window.sessionStorage.setItem(dismissedKey, signature);
    } catch {
      // Storage blocked: hide for this page view only.
    }

    setDismissed(signature);
  };

  return {
    months,
    isVisible: months.length > 0 && dismissed !== signature,
    hide,
  };
}
